export type LegalBlockStyle = 'h1' | 'h2' | 'h3' | 'p' | 'li';

export type LegalBlock = {
  style: LegalBlockStyle;
  text: string;
};

export type LegalDocumentMeta = {
  slug: string;
  title: string;
  description: string;
  blocks: LegalBlock[];
};

function headingLevel(text: string): LegalBlockStyle | null {
  if (/^MADDE\s+\d+/i.test(text)) return 'h2';
  if (/^SATICI\s+(BİLGİLERİ|İLETİŞİM)/i.test(text)) return 'h3';
  if (/^ALICI\s+BİLGİLERİ/i.test(text)) return 'h3';
  if (/^\d+\.\s+[A-ZÇĞİÖŞÜ]/.test(text) && text.length < 120) return 'h2';
  if (/^\d+\)\s/.test(text)) return 'h3';
  if (/^[A-ZÇĞİÖŞÜ0-9\s–\-]+$/.test(text) && text.length < 80 && !text.includes('.')) {
    return 'h1';
  }
  return null;
}

export function normalizeLegalBlocks(blocks: LegalBlock[]): LegalBlock[] {
  return blocks.map((block) => {
    if (block.style !== 'p') return block;
    const detected = headingLevel(block.text);
    return detected ? { ...block, style: detected } : block;
  });
}

export function groupLegalBlocks(blocks: LegalBlock[]) {
  const normalized = normalizeLegalBlocks(blocks);
  const groups: Array<
    | { type: 'heading'; level: LegalBlockStyle; text: string }
    | { type: 'paragraph'; text: string }
    | { type: 'list'; items: string[] }
  > = [];

  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      groups.push({ type: 'list', items: [...listBuffer] });
      listBuffer = [];
    }
  };

  for (const block of normalized) {
    if (block.style === 'li') {
      listBuffer.push(block.text);
      continue;
    }

    flushList();

    if (block.style === 'h1' || block.style === 'h2' || block.style === 'h3') {
      groups.push({ type: 'heading', level: block.style, text: block.text });
    } else {
      groups.push({ type: 'paragraph', text: block.text });
    }
  }

  flushList();
  return groups;
}

export function linkifyLegalText(text: string) {
  const parts: Array<{ type: 'text' | 'link'; value: string; href?: string }> = [];
  const regex =
    /(guvenozelsaglik@gmail\.com|balikesirguvenozel@hs06\.kep\.tr|info@irgilakademi\.com|\+90\s?553\s?538\s?24\s?62|www\.healthshoplongevity\.com)/gi;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }

    const value = match[0];
    let href = '';
    if (value.includes('@')) {
      href = `mailto:${value.replace(/^E-posta:?/i, '').replace(/^KEP:?/i, '').trim()}`;
    } else if (value.includes('healthshoplongevity')) {
      href = 'https://www.healthshoplongevity.com';
    } else {
      href = 'tel:+905535382462';
    }

    parts.push({ type: 'link', value, href });
    lastIndex = match.index + value.length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text' as const, value: text }];
}
